import {createObjectIntentType} from '../intent';
import type {Style} from '../style/types';
import {lazyStyleIntent} from '../style/intents';
import {assignConditionalProperty, mergeConditionalProperty} from '../util';
import {$compiled} from './constants';
import {
  DEFAULT_KEY,
  getTokenId,
  getTokenData,
  getTokenMode,
  getTokenPath,
  isToken,
  isTokens,
} from './token';
import type {Token} from './types';

function assertTokenIsNotDeprecated(
  token: Token.Value | Token.AbstractMap,
): void {
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.NODE_ENV !== 'script' &&
    getTokenData(token).deprecated
  ) {
    throw new Error(`Token "${getTokenPath(token)}" is deprecated.`);
  }
}

function assignTokensInternal<T>(
  input: Token.AbstractMap | Token.Value,
  assignment: Partial<T>,
  overrides: Token.OverrideMap,
  condition: string,
  isDefault = false,
): Token.OverrideMap {
  const isObject = assignment && typeof assignment === 'object';
  if (isToken(input)) {
    assertTokenIsNotDeprecated(input);

    if (getTokenMode(input) === 'static') {
      throw new Error(
        `Cannot assign value to static token "${getTokenPath(input)}"`,
      );
    }
    // Assign in the following cases:
    // - The assignment is not an object
    // - The assignment is a token
    // - The current input is a leaf node (and does not contain nested tokens)
    // - The object was passed to a $default property
    if (!isObject || isToken(assignment) || !isTokens(input) || isDefault) {
      if (isToken(assignment) || isTokens(assignment)) {
        assertTokenIsNotDeprecated(assignment);
      }

      assignConditionalProperty(
        overrides,
        getTokenId(input),
        assignment as unknown,
        condition,
      );
    }
  }
  if (isObject) {
    Object.keys(assignment).forEach((key) => {
      if (key in input) {
        assignTokensInternal(
          key === DEFAULT_KEY
            ? input
            : (input as unknown as Record<string, Token.Value>)[key],
          assignment[key as keyof T] as Token.OverrideMap,
          overrides,
          condition,
          key === DEFAULT_KEY,
        );
      }
    });
  }
  return overrides;
}

function assignOverrideMap(
  target: Token.OverrideMap,
  source: Token.OverrideMap,
): void {
  const keys = Object.keys(source);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    mergeConditionalProperty(target, key, source[key]);
  }
}

export const assignTokensIntent = createObjectIntentType(
  'assignTokens',
  (decorate) =>
    (
      sailOverrides: Token.OverrideMap,
      stylexOverrides?: Style.Intent,
    ): Token.OverrideIntent<{[$compiled]?: Style.Intent[]}> => {
      if (lazyStyleIntent.isIntent(stylexOverrides)) {
        Object.defineProperty(sailOverrides, $compiled, {
          value: [stylexOverrides],
          enumerable: false,
          writable: false,
        });
      }

      return decorate(sailOverrides);
    },
  (target, source) => {
    assignOverrideMap(target, source);

    if (source[$compiled]) {
      target[$compiled] = target[$compiled] || [];
      target[$compiled].push(...source[$compiled]);
    }
  },
);

/**
 * Merges the provided override intents into a single intent.
 */
export function mergeOverrideIntents(
  overrideIntents: Token.OverrideIntent[],
): Token.OverrideIntent {
  const result: Token.OverrideMap = {};
  for (let i = 0; i < overrideIntents.length; i++) {
    assignOverrideMap(result, overrideIntents[i]);
  }
  return assignTokensIntent(result);
}

/**
 * Creates an intent to assign a token or tokens to new values when a provided
 * condition is true.
 */
export function assignConditionalTokens<T>(
  ...args: [condition: string, input: T, assignment: Token.Assignment<T>]
): Token.OverrideIntent {
  const [
    condition,
    input,
    assignment,
    // @ts-expect-error - We're not exposing the StyleX token assignment as a public API.
    // This parameter will be added by the Sail X babel plugin during the build process.
    __stylexOverride,
  ] = args;
  // Tokens from '@sail/tokens' contain a $instances property that maps token
  // keys to their respective token values.
  const tokens =
    typeof input === 'object' && input !== null && '$instances' in input
      ? input.$instances
      : input;

  return assignTokensIntent(
    assignTokensInternal(
      tokens as unknown as Token.Value,
      assignment as Partial<T>,
      {},
      condition,
    ),
    __stylexOverride,
  );
}

/**
 * Creates an intent to assign a token or tokens to new values.
 *
 * @see https://trailhead.corp.stripe.com/docs/sail-ui/api/tokens/assigntokens
 */
export function assignTokens<T>(
  ...args: [input: T, assignment: Token.Assignment<T>]
): Token.OverrideIntent {
  return assignConditionalTokens('', ...args);
}
